import type {SkipToken} from '@reduxjs/toolkit/query'
import {skipToken} from '@reduxjs/toolkit/query'
import {useEffect} from 'react'

import {useBranch} from '../../../../../hooks/routes'
import {getBranchLocator, getPagerLocator} from '../../../../../rest/locators'
import {type GetAllBuildsApiArg, restApi} from '../../../../../services/rest'
import type {BuildId} from '../../../../../types'
import {emptyArray} from '../../../../../utils/empty'
import {subscribeOnBuildTypeEvents} from '../../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_ADDED_TO_QUEUE,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../../utils/subscriptionEvents'
import {hasItemsOnPage} from '../../../../common/Pager/Pager.utils'
import {DEFAULT_PAGE_SIZE} from '../../../../common/PagerNew/PagerNew.consts'
import {useCurrentPageState} from '../../../../common/PagerNew/PagerNew.hooks'
import type {PagerProps} from '../../../../common/PagerNew/PagerNew.types'
import {COUNT_VISIBLE_SCOPES} from '../../../BuildPage/BuildTestsTab/TestScopesView/TestScopesView.utils'
import type {pipelineHeadFragment$key} from '../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineHead, usePipelineHeadId} from '../../hooks/pipeline'

function usePipelineHistoryPager(
  runIds: BuildId[],
  hasMore: boolean,
  loadedLessThanRequested: boolean,
): PagerProps {
  const [currentPage, changeCurrentPage] = useCurrentPageState()

  const total = runIds?.length ?? 0

  const show =
    hasItemsOnPage(currentPage, {
      pageSize: COUNT_VISIBLE_SCOPES,
      total,
    }) ||
    !loadedLessThanRequested ||
    currentPage !== 1 ||
    !hasMore

  return {
    show,
    currentPage,
    changeCurrentPage,
    openTotal: hasMore && !loadedLessThanRequested,
    total,
  }
}

export function usePipelineHistoryArg(
  pipelineHeadKey: pipelineHeadFragment$key | null,
  pageSize: number = DEFAULT_PAGE_SIZE,
): GetAllBuildsApiArg | SkipToken {
  const [page] = useCurrentPageState()
  const headId = usePipelineHeadId(pipelineHeadKey)
  const branch = useBranch()

  const branchLocator = getBranchLocator(branch, true)
  const pagerLocator = getPagerLocator(page, pageSize)

  return headId != null
    ? {
        locator: [
          `defaultFilter:false,buildType:(id:${headId}),state:any`,
          branchLocator,
          pagerLocator,
        ]
          .filter(Boolean)
          .join(','),
        fields:
          'build(id,number,state,status,waitReason,startEstimate,finishEstimate' +
          ',canceledInfo,statusText,history,startDate,finishDate,queuedDate,limitedChangesCount($optional)' +
          ',failedToStart,buildType(id),branchName,defaultBranch' +
          ',running-info(percentageComplete,elapsedSeconds,estimatedTotalSeconds,leftSeconds,overtime)),nextHref',
        options: {pageSize},
      }
    : skipToken
}

export function usePipelineHistory(pipelineHeadKey: pipelineHeadFragment$key | null) {
  const head = usePipelineHead(pipelineHeadKey)
  const headInternalId = head?.internalId

  const arg = usePipelineHistoryArg(pipelineHeadKey)
  const {runIds, hasMore, loadedLessThanRequested, refetch} =
    restApi.endpoints.getAllBuildsNormalized.useQuery(arg, {
      selectFromResult: ({data}) => ({
        runIds: (data?.result ?? emptyArray) as BuildId[],
        hasMore: data?.hasMore ?? false,
        loadedLessThanRequested: data?.loadedLessThanRequested ?? true,
      }),
    })

  const pager = usePipelineHistoryPager(runIds, hasMore, loadedLessThanRequested)

  useEffect(
    () =>
      headInternalId != null
        ? subscribeOnBuildTypeEvents(
            headInternalId,
            [
              BUILD_TYPE_ADDED_TO_QUEUE,
              BUILD_TYPE_REMOVED_FROM_QUEUE,
              BUILD_STARTED,
              BUILD_FINISHED,
              BUILD_INTERRUPTED,
            ],
            refetch,
          )
        : undefined,
    [headInternalId, refetch],
  )

  return {ids: runIds, pager}
}
